/**
 * @fileoverview Affiliate breakdown dialog (MOR-438)
 *
 * Month-by-month fee working for one agreement, grouped by firm: the contract
 * terms in force (rate timeline + adjustment), headline totals, then each
 * firm's audit table.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { Badge } from "@/ui/primitives/badge";
import { Separator } from "@/ui/primitives/separator";
import { Skeleton } from "@/ui/primitives/skeleton";
import { AlertTriangle } from "lucide-react";
import { AffiliateFeeTable } from "./AffiliateFeeTable";
import { adjustmentExplainer } from "./affiliateCopy";
import { formatAudExact, formatDate } from "./billingFormat";
import { RateScheduleTimeline, StatTile } from "./billingShared";

export function BreakdownDialog({
  agreementId,
  onClose,
}: {
  agreementId: Id<"affiliateAgreements">;
  onClose: () => void;
}) {
  const { data: breakdown } = useQuery(
    convexQuery(api.admin.billing.getAffiliateBreakdown, { agreementId }),
  );

  const netAudCents = breakdown
    ? breakdown.totals.grossAudCents - breakdown.totals.feeAudCents
    : 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] min-w-0 overflow-y-auto overflow-x-hidden sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {breakdown
              ? `${breakdown.agreement.name} — affiliate breakdown`
              : "Affiliate breakdown"}
          </DialogTitle>
          <DialogDescription>
            Month-by-month working per firm since its conversion date. Amounts
            are ex GST.
          </DialogDescription>
        </DialogHeader>

        {!breakdown ? (
          <div className="flex flex-col gap-3 py-2">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-6">
            {/* Contract terms in force, so the numbers can be checked. */}
            <div className="flex flex-col gap-3 rounded-xl border bg-muted/30 p-4">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Rate schedule (months since conversion)
              </span>
              <RateScheduleTimeline
                schedule={breakdown.agreement.rateSchedule}
                className="max-w-2xl"
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                {adjustmentExplainer(breakdown.agreement.grossMarginAdjustment)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatTile
                label="Total gross"
                value={formatAudExact(breakdown.totals.grossAudCents)}
              />
              <StatTile
                label="Total adjustment"
                value={
                  breakdown.totals.adjustmentAudCents > 0
                    ? `−${formatAudExact(breakdown.totals.adjustmentAudCents)}`
                    : "—"
                }
              />
              <StatTile
                label="Affiliate fee"
                value={formatAudExact(breakdown.totals.feeAudCents)}
              />
              <StatTile
                label="Net to Morgan"
                value={formatAudExact(netAudCents)}
                tone="positive"
              />
            </div>

            {breakdown.unconvertedOrgs.length > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Not yet converted, so no fee is accruing:{" "}
                  <span className="font-medium">
                    {breakdown.unconvertedOrgs.map((o) => o.orgName).join(", ")}
                  </span>
                </span>
              </div>
            )}

            {breakdown.perFirm.map((firm, i) => (
              <div key={firm.orgId} className="flex flex-col gap-3">
                {i > 0 && <Separator />}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <h3 className="text-sm font-semibold">{firm.orgName}</h3>
                    {firm.convertedAt && (
                      <span className="text-xs text-muted-foreground">
                        converted {formatDate(firm.convertedAt)}
                      </span>
                    )}
                    {firm.churned && (
                      <Badge color="red" variant="soft" size="sm">
                        Churned
                      </Badge>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Fee to date{" "}
                    <span className="font-semibold tabular-nums text-foreground">
                      {formatAudExact(firm.subtotals.feeAudCents)}
                    </span>
                  </span>
                </div>
                <AffiliateFeeTable
                  rows={firm.rows}
                  subtotals={firm.subtotals}
                />
              </div>
            ))}
            {breakdown.perFirm.length === 0 && (
              <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                No converted firms are linked to this agreement yet. Link a firm
                and move it off the Pilot Program to start the fee clock.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
